<template>
  <d-form>
    <d-form-row class="form-group mb-1">
      <d-col cols="12" md="6">
        <div class="d-flex justify-content-between w-100">
          <label class="d-flex align-items-center mb-0">
            {{ $t('wordSeparator.form.text.label') }}
          </label>
          <d-col
            class="form-group d-flex mb-0 p-0 align-items-end justify-content-end"
          >
            <div class="d-flex flex-row align-items-center">
              <label
                class="generate-multiple-label mb-0"
                for="feInputNewsletter"
              >
                {{ $t('wordSeparator.form.variations.label') }}
              </label>
              <d-checkbox
                toggle
                :checked="this.variations"
                class="custom-toggle-sm toggle-padding ml-2 mb-0"
                @change="toggleVariations"
              >
              </d-checkbox>
            </div>
          </d-col>
        </div>
      </d-col>
      <d-col cols="12" md="6" class="d-none d-md-flex flex-column">
        <copy-text-title
          :onCopyText="copyText"
          :title="$t('wordSeparator.form.result.label')"
        />
      </d-col>
    </d-form-row>
    <d-form-row class="form-group mb-1">
      <d-col cols="12" md="6">
        <editor
          :model.sync="model.text"
          ref="editor"
          reference="text"
          @ready="setQuill"
          :editorOption="{
            modules: {
              toolbar: null,
              keyboard: {
                bindings: {
                  bold: {
                    key: 'B',
                    shortKey: true,
                    handler: selectWord,
                  },
                },
              },
            },
            placeholder: $t('wordSeparator.form.text.placeholder'),
          }"
        />
      </d-col>
      <d-col cols="12" md="6" class="d-flex d-md-none flex-column my-2">
        <copy-text-title
          :onCopyText="copyText"
          :title="$t('wordSeparator.form.result.label')"
        />
      </d-col>
      <d-col cols="12" md="6">
        <editor
          :model.sync="model.result"
          reference="result"
          :editorOption="{
            modules: { toolbar: null },
            placeholder: $t('wordSeparator.form.result.placeholder'),
          }"
        />
      </d-col>
    </d-form-row>
    <d-form-row class="form-group">
      <d-col cols="12" md="6" class="pt-1">
        <div class="d-flex align-items-center w-100">
          <div class="d-flex align-items-center flex-shrink-0">
            <label class="mb-0 mr-2 min-word-length-label" for="minWordLength">
              {{ $t('wordSeparator.form.minWordLength.label') }}
              <i
                id="tooltip-min-word-length"
                class="fas fa-info-circle text-muted ml-1"
                style="cursor: pointer"
              />
              <d-tooltip target="tooltip-min-word-length" placement="top">
                {{ $t('wordSeparator.form.minWordLength.tooltip') }}
              </d-tooltip>
            </label>
            <d-form-input
              id="minWordLength"
              type="number"
              v-model.number="minWordLength"
              min="1"
              class="min-word-length-input"
              @change="onMinWordLengthChange"
            />
          </div>
          <div class="flex-grow-1">
            <action-buttons :onGenerate="generateResult" :onClear="cleanText" />
          </div>
        </div>
      </d-col>
      <d-col
        v-if="!variations"
        cols="12"
        md="6"
        class="d-flex justify-content-between md-mt-0 pt-0"
      >
        <label class="w-75">
          {{ $t('wordSeparator.form.repeatedWords.label') }}
          {{ model.repeatedWords.join(', ') }}
        </label>
        <label>
          {{ $t('wordSeparator.form.totalWords.label') }}
          {{ model.totalWords }}
        </label>
      </d-col>
    </d-form-row>

    <d-form-row class="form-group" v-if="variations">
      <d-col cols="12" class="d-flex justify-content-between md-mt-0 mt-1">
        <word-separator-list-table
          :listMultipleCases="model.listMultipleCases"
        />
      </d-col>
    </d-form-row>
  </d-form>
</template>

<script>
import { htmlToText } from 'html-to-text';

import Editor from '_shared/Editor.vue';
import CopyTextTitle from '_shared/CopyTextTitle.vue';
import ActionButtons from '_shared/ActionButtons.vue';
import WordSeparatorListTable from './WordSeparatorListTable.vue';

const UPPERCASE_LETTERS = 'A-ZÀ-ÝÇÂÊÎÔÛÃÕÄËÏÖÜ';
const UPPERCASE_WORD_PATTERN = new RegExp(
  `(?:^|\\s)([${UPPERCASE_LETTERS}]+(?:-[${UPPERCASE_LETTERS}0-9]+)*)(?=\\s|$)`,
  'gu',
);
const NON_WORD_CHARS_PATTERN = /[^A-Za-zÀ-ÿ0-9-]+/g;
const TRAILING_HYPHEN_PATTERN = /-(?=\s|$)/g;

export default {
  name: 'word-separator-form',
  components: {
    Editor,
    WordSeparatorListTable,
    CopyTextTitle,
    ActionButtons,
  },
  data() {
    return {
      timer: null,
      quill: null,
      model: {
        text: '',
        list: [],
        listMultipleCases: [],
        repeatedWords: [],
        totalWords: 0,
        result: '',
      },
      variations: false,
      minWordLength: 3,
    };
  },
  methods: {
    cleanText() {
      this.model.text = '';
      this.cleanResult();
    },
    cleanResult() {
      this.model.repeatedWords = [];
      this.model.listMultipleCases = [];
      this.model.list = [];
      this.model.totalWords = 0;
      this.model.result = '';
    },
    setQuill(quill) {
      this.quill = quill;
    },
    selectWord(range) {
      const word =
        (document.selection && document.selection.createRange().text) ||
        (window.getSelection && window.getSelection().toString());
      const formatted = word.toUpperCase();

      if (this.model.list.includes(formatted)) {
        // eslint-disable-next-line
        alert(this.$t('wordSeparator.alert.repeatedWord'));
      } else {
        this.model.list.push(formatted);

        const { index, length } = range;
        const options = [];
        if (index > 0) {
          options.push({ retain: index });
        }
        options.push({ delete: length });
        options.push({ insert: formatted });
        this.quill.updateContents({
          ops: options,
        });
        this.quill.formatText(index, length, 'bold', true);
      }
    },
    generateResult() {
      this.normalizeMinWordLength();
      if (this.model.result !== '') this.cleanResult();
      if (this.variations) {
        const cleanedHtml = this.model.text.replace(/(<br>)+/g, '<p><br></p>');
        const parts = cleanedHtml.split('<p><br></p>');

        parts.forEach(part => {
          const { list, totalWords } = this.extractUppercaseTextFromHTML(part);
          if (list.length) {
            this.model.listMultipleCases.push({
              repeatedWords: this.model.repeatedWords,
              totalWord: totalWords,
            });
          }

          this.model.result += part;
          this.model.result += '<p><br></p>';
          this.model.result += list.join('<br>');
          this.model.result += '<p><br></p><p><br></p><p><br></p>';
        });
      } else {
        const { list, totalWords } = this.extractUppercaseTextFromHTML(
          this.model.text,
        );
        this.model.list = list;
        this.model.totalWords = totalWords;
        this.model.result += list.join('<br>');
        this.model.result += '<p><br></p><p><br></p><p><br></p>';
      }
    },
    convertHtmlToText(html) {
      return htmlToText(html, {
        wordwrap: false,
        selectors: [
          { selector: 'br', format: 'inlineBreak' },
          {
            selector: 'p',
            options: { leadingLineBreaks: 1, trailingLineBreaks: 1 },
          },
        ],
        preserveNewlines: true,
      });
    },
    copyText() {
      const plainText = this.convertHtmlToText(this.model.result);
      const plainTextFormatted = plainText.replace(/\n+/g, match => {
        const count = match.length;
        if (count === 2) return '\n';
        if (count === 5) return '\n\n';
        if (count > 5) return '\n\n\n';
        return match;
      });

      navigator.clipboard.writeText(plainTextFormatted).catch(err => {
        console.error('Erro ao copiar texto:', err);
      });
    },
    normalizeMinWordLength() {
      const parsed = parseInt(this.minWordLength, 10);
      this.minWordLength = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    },
    onMinWordLengthChange() {
      this.normalizeMinWordLength();
      if (this.model.text !== '') this.generateResult();
    },
    removeShortAndRoman(list) {
      const romanNumerals =
        /^M{0,3}(?:C[MD]|D?C{0,3})(?:X[CL]|L?X{0,3})(?:I[XV]|V?I{0,3})$/i;
      return list.filter(
        word => word.length >= this.minWordLength && !romanNumerals.test(word),
      );
    },
    extractUppercaseTextFromHTML(html) {
      const text = this.normalizeHtmlForWordExtraction(html);

      const uppercaseMatches = [...text.matchAll(UPPERCASE_WORD_PATTERN)].map(
        ([, word]) => word,
      );

      this.model.repeatedWords = this.getRepeatedWords(uppercaseMatches);
      const filtered = this.removeShortAndRoman(uppercaseMatches);
      const list = [...new Set(filtered)];

      return { list, totalWords: filtered.length };
    },
    normalizeHtmlForWordExtraction(html) {
      const textOnly = this.convertHtmlToText(html).replace(/\s+/g, ' ');
      const textWithoutQuotes = textOnly.replace(/['"`´‘’“”]/g, '');

      const withoutPunctuation = textWithoutQuotes.replace(
        NON_WORD_CHARS_PATTERN,
        ' ',
      );

      return withoutPunctuation.replace(TRAILING_HYPHEN_PATTERN, '');
    },
    getRepeatedWords(array) {
      const repeatedWords = [
        ...new Set(
          array.filter((item, index, arr) => arr.indexOf(item) !== index),
        ),
      ];

      return this.removeShortAndRoman(repeatedWords);
    },
    toggleVariations() {
      this.variations = !this.variations;
      if (this.model.text !== '') this.generateResult();
    },
  },
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
  &:hover {
    color: red;
  }
}
.text-area {
  height: 200px;
}
.min-word-length-label {
  font-size: 0.75rem;
}
.min-word-length-input {
  font-size: 0.75rem;
  width: 5rem;
  height: 1.75rem;
}
.generate-multiple-label {
  font-size: 0.75rem;
}
.toggle-padding {
  padding-left: 2.2rem;
}
</style>
